import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiClient } from "../lib/api";

/**
 * useLiveTrips — the ONE data path for live TP trips.
 *
 * Consumed by TPTripsPanel (admin Ops board) and The Floor (map halos + focus
 * card). One SSE stream, one poll fallback, one filter for the live board.
 * Any surface that needs to know "what trips are moving right now" reads
 * from this hook. Zero duplicate fetching.
 *
 * Endpoints used (all pre-existing):
 *   GET   /tpt/trips                — full list
 *   SSE   /tpt/stream/trips         — trip_update events (token via ?access_token=)
 *   POST  /tpt/trips/{id}/assign    — canonical assignment (VA + TP + auto-assigner)
 *
 * Options:
 *   pollMs                    safety-net poll (default 30000)
 *   liveOnly                  filter out terminal trips (default true)
 *   terminalGraceMs           keep terminal trips visible for N ms (default 60000)
 *   onArrival(trip)           fires once per newly-observed trip_id
 *   onStateChange(trip,prev)  fires when an existing trip's status changes
 */
const TP = "/tpt";
// Real statuses observed in production db.trips (differ from canonical docs):
//   REQUESTED · TP_ACCEPTED · ASSIGNED · ACCEPTED · EN_ROUTE · ON_TRIP
//   COMPLETED · EXPIRED_OFFER · CANCELED · CANCELED_BY_RIDER
const TERMINAL = new Set([
  "COMPLETED", "CANCELED", "CANCELED_BY_RIDER", "CANCELLED",
  "NO_SHOW_CONFIRMED", "EXPIRED_OFFER", "OFFER_EXHAUSTED",
]);

export function useLiveTrips({
  pollMs = 30000,
  liveOnly = true,
  terminalGraceMs = 60000,
  onArrival,
  onStateChange,
} = {}) {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [connected, setConnected] = useState(false);
  const seenRef = useRef(new Map()); // trip_id → last-status
  const arrivalRef = useRef(onArrival);
  const stateChangeRef = useRef(onStateChange);
  useEffect(() => { arrivalRef.current = onArrival; }, [onArrival]);
  useEffect(() => { stateChangeRef.current = onStateChange; }, [onStateChange]);

  const load = useCallback(async () => {
    try {
      const { data } = await apiClient.get(`${TP}/trips`);
      const rows = Array.isArray(data) ? data : [];
      setTrips(rows);
      setFetchedAt(new Date());
      // Emit arrival + state-change events
      const nextSeen = new Map();
      rows.forEach((t) => {
        const id = t.trip_id;
        if (!id) return;
        nextSeen.set(id, t.status);
        const prev = seenRef.current.get(id);
        if (prev === undefined) {
          if (arrivalRef.current) { try { arrivalRef.current(t); } catch { /* noop */ } }
        } else if (prev !== t.status) {
          if (stateChangeRef.current) { try { stateChangeRef.current(t, prev); } catch { /* noop */ } }
        }
      });
      seenRef.current = nextSeen;
    } catch (_e) { /* silent — SSE + next poll recover */ }
    finally { setLoading(false); }
  }, []);

  // First load + poll safety net
  useEffect(() => {
    load();
    if (!pollMs) return undefined;
    const t = setInterval(load, pollMs);
    return () => clearInterval(t);
  }, [load, pollMs]);

  // SSE — same URL TPTripsPanel used; EventSource can't send Authorization
  // header so token rides on the query string (backend accepts both).
  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL;
    if (!base) return undefined;
    const token = (() => { try { return localStorage.getItem("astat_admin_token") || ""; } catch { return ""; } })();
    const url = `${base}/api${TP}/stream/trips?access_token=${encodeURIComponent(token)}`;
    let es;
    try {
      es = new EventSource(url);
      es.onopen = () => setConnected(true);
      es.addEventListener("trip_update", () => { load(); });
      es.onerror = () => setConnected(false);
    } catch { /* ignore */ }
    return () => { if (es) { try { es.close(); } catch { /* noop */ } } };
  }, [load]);

  // Filtered view for the live board (drop terminals after grace period)
  const filtered = useMemo(() => {
    if (!liveOnly) return trips;
    const cutoff = Date.now() - terminalGraceMs;
    return trips.filter((t) => {
      if (!TERMINAL.has(t.status)) return true;
      const ts = t.updated_at || t.completed_at;
      if (!ts) return false;
      try { return new Date(ts).getTime() >= cutoff; } catch { return false; }
    });
  }, [trips, liveOnly, terminalGraceMs]);

  // Assignment through the single canonical path.
  const assign = useCallback(async (tripId, payload) => {
    // payload examples:
    //   { tp_id, driver_id, dispatch_at }              (manual)
    //   { mode: "auto_assigner" }                      (Nova picks nearest cleared driver)
    if (!tripId) return { ok: false, error: "missing trip id" };
    try {
      const { data } = await apiClient.post(`${TP}/trips/${tripId}/assign`, payload || {});
      // SSE will fire a trip_update; still refetch for latency safety.
      load();
      return { ok: true, ...(data || {}) };
    } catch (e) {
      return { ok: false, error: e?.response?.data?.detail || String(e) };
    }
  }, [load]);

  // Broadcast — separate endpoint/schema from assign (scope: tp|tps|network|custom).
  // Floor's "broadcast" button fans out to the whole cleared network by default.
  const broadcastTrip = useCallback(async (tripId, payload) => {
    if (!tripId) return { ok: false, error: "missing trip id" };
    try {
      const { data } = await apiClient.post(`${TP}/trips/${tripId}/broadcast`, payload || { scope: "network", include_independent: true });
      load();
      return { ok: true, ...(data || {}) };
    } catch (e) {
      return { ok: false, error: e?.response?.data?.detail || String(e) };
    }
  }, [load]);

  // Fan a trip out to N eligible TPs — the multi-TP marketplace gate. Same
  // canonical endpoint Live Ops uses (`POST /tpt/trips/{id}/offer`).
  const offerToTps = useCallback(async (tripId, { tp_ids, ttl_minutes, note } = {}) => {
    if (!tripId || !Array.isArray(tp_ids) || tp_ids.length === 0) {
      return { ok: false, error: "select at least one TP" };
    }
    try {
      const { data } = await apiClient.post(`${TP}/trips/${tripId}/offer`, { tp_ids, ttl_minutes, note });
      load();
      return { ok: true, ...(data || {}) };
    } catch (e) {
      return { ok: false, error: e?.response?.data?.detail || String(e) };
    }
  }, [load]);

  const findTrip = useCallback((tripId) => trips.find((t) => t.trip_id === tripId) || null, [trips]);

  return {
    trips,             // full list from the server (all statuses)
    liveTrips: filtered, // filtered for live-board consumption
    loading,
    fetchedAt,
    connected,        // SSE connected boolean (UI can badge "LIVE")
    assign,
    broadcastTrip,
    offerToTps,
    refetch: load,
    findTrip,
  };
}

/**
 * Canonical acceptance chain — the REAL 7-gate lifecycle, used by the
 * Floor's focus card. A trip may take the marketplace path (fan out to
 * TPs, one accepts, then a driver is picked) OR the direct path (VA
 * picks a TP + driver in one `/assign` call, skipping the fan-out).
 * Both are legitimate — the chain must be honest about which happened,
 * not force every trip through a gate it never used.
 */
export const ACCEPTANCE_CHAIN = [
  { key: "kaizen",    label: "Kaizen Offer" },   // implicit — offer was in the incoming lane
  { key: "va",        label: "VA Accept" },      // trip now exists in db.trips
  { key: "tp_offer",  label: "Offered to TP(s)" },// status → OFFERED (fan-out in flight)
  { key: "tp_accept", label: "TP Accepted" },     // status → TP_ACCEPTED (needs a driver)
  { key: "assigned",  label: "Driver Assigned" }, // status → ASSIGNED (SMS sent, considering)
  { key: "driver",    label: "Driver Accepted" }, // status → ACCEPTED (locked)
  { key: "locked",    label: "LOCKED · Cycle" },  // EN_ROUTE → ARRIVED_PU → ON_TRIP → ARRIVED_DO → COMPLETED
];

/**
 * Given a trip document, return which acceptance-chain nodes are complete,
 * which are skipped (took the direct-assign path, never went through the
 * TP marketplace), and which node is currently active.
 */
export function acceptanceState(trip) {
  if (!trip) return { completed: [], active: null, skipped: [] };
  const s = String(trip.status || "").toUpperCase();
  const completed = ["kaizen", "va"];
  const skipped = [];
  // A non-empty offer_pool means this trip actually went through the
  // multi-TP fan-out at some point in its life, even if it's moved past
  // OFFERED/TP_ACCEPTED since. No pool → the VA assigned directly.
  const wentThroughFanout = Array.isArray(trip.offer_pool) && trip.offer_pool.length > 0;
  const markTpGates = () => { if (wentThroughFanout) completed.push("tp_offer", "tp_accept"); else skipped.push("tp_offer", "tp_accept"); };

  let active = "tp_offer";

  if (s === "REQUESTED" || s === "PENDING") {
    active = "tp_offer"; // VA may either offer-to-TPs or assign a driver directly
  } else if (s === "OFFERED") {
    completed.push("tp_offer");
    active = "tp_accept";
  } else if (s === "TP_ACCEPTED") {
    completed.push("tp_offer", "tp_accept");
    active = "assigned";
  } else if (s === "ASSIGNED") {
    markTpGates();
    completed.push("assigned");
    active = "driver";
  } else if (s === "ACCEPTED") {
    markTpGates();
    completed.push("assigned", "driver");
    active = "locked";
  } else if (["EN_ROUTE", "ARRIVED_PU", "ON_TRIP", "ARRIVED_DO"].includes(s)) {
    markTpGates();
    completed.push("assigned", "driver");
    active = "locked";
  } else if (s === "COMPLETED") {
    markTpGates();
    completed.push("assigned", "driver", "locked");
    active = null;
  } else if (["EXPIRED_OFFER", "OFFER_EXHAUSTED"].includes(s)) {
    if (wentThroughFanout) completed.push("tp_offer");
    active = null;
  } else if (["CANCELED", "CANCELED_BY_RIDER", "CANCELLED", "NO_SHOW_CONFIRMED"].includes(s)) {
    active = null;
  }
  return { completed, active, skipped };
}

/**
 * getOperationalState — read the backend's single-source-of-truth envelope
 * (attached server-side by operational_state.py on every /tpt/trips row).
 * Falls back to a local equivalent only if a trip somehow arrives without
 * one (e.g. a stale cached row) — the server value always wins when present.
 */
export function getOperationalState(trip) {
  if (trip?.operational_state) return trip.operational_state;
  const chain = acceptanceState(trip);
  return {
    state: null, owner: null, response: null, mode: null,
    lifecycle: "watching", chain, attention_score: 0,
    workspace_hint: statusLabel(trip?.status),
  };
}

/** Color a trip halo per its cycle stage — used by the map layer. */
export function haloColorForStatus(status) {
  const s = String(status || "").toUpperCase();
  if (s === "REQUESTED" || s === "PENDING")     return "#f59e0b"; // amber — awaiting TP
  if (s === "OFFERED")                           return "#a78bfa"; // violet — TP(s) considering
  if (s === "TP_ACCEPTED")                       return "#eab308"; // gold — TP claimed, needs driver
  if (s === "ASSIGNED")                          return "#4bb8f8"; // blue — driver considering
  if (s === "ACCEPTED")                          return "#7fd8b7"; // green — locked
  if (s === "EN_ROUTE")                          return "#22d3ee"; // cyan — moving
  if (s === "ARRIVED_PU")                        return "#a8e6cf";
  if (s === "ON_TRIP")                           return "#34d399"; // vivid green — active ride
  if (s === "ARRIVED_DO")                        return "#a8e6cf";
  if (s === "COMPLETED")                         return "#64748b"; // slate — done
  if (["EXPIRED_OFFER", "OFFER_EXHAUSTED"].includes(s)) return "#ef4444"; // red — no driver took it
  if (["CANCELED", "CANCELED_BY_RIDER", "CANCELLED"].includes(s)) return "#94a3b8";
  if (s === "NO_SHOW_CONFIRMED")                 return "#ef4444";
  return "#94a3b8";
}

/** Human-readable status label for the focus card + tooltips. */
export function statusLabel(status) {
  const s = String(status || "").toUpperCase();
  const map = {
    REQUESTED: "awaiting TP",
    PENDING: "awaiting TP",
    OFFERED: "TP(s) considering",
    TP_ACCEPTED: "TP claimed · needs driver",
    ASSIGNED: "driver considering",
    ACCEPTED: "locked",
    EN_ROUTE: "en route",
    ARRIVED_PU: "at pickup",
    ON_TRIP: "on trip",
    ARRIVED_DO: "at dropoff",
    COMPLETED: "completed",
    EXPIRED_OFFER: "offer expired",
    OFFER_EXHAUSTED: "no takers",
    CANCELED: "canceled",
    CANCELED_BY_RIDER: "rider canceled",
    NO_SHOW_CONFIRMED: "no-show",
  };
  return map[s] || s.toLowerCase().replace(/_/g, " ");
}
